import { Color } from "@repo/app-types/constants/colors";
import {
  CustomFieldResourceType,
  CustomFieldType,
  customFieldResourceTypeLabels,
  type CustomFieldDefinition,
} from "@repo/app-types/entities/customField";
import {
  greenRedYellowResultTypeCategory,
  isContactCustomFieldPresenceOutcomeDefinition,
  isFunnelOutcomeDefinition,
  isRevenueOutcomeDefinition,
  type ContactCustomFieldPresenceOutcomeDefinition,
  type FunnelOutcomeDefinition,
  type FunnelOutcomeStage,
  type OutcomeDefinition,
  type OutcomeGreenRedYellowResultCategory,
  revenueOutcomeResults,
  type RevenueOutcomeDefinition,
} from "@repo/app-types/entities/outcome";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { IconButton } from "@repo/ui/Buttons/iconButton";
import { Combobox } from "@repo/ui/combobox/combobox";
import { ComboboxContent } from "@repo/ui/combobox/comboboxContent";
import { ComboboxInput } from "@repo/ui/combobox/comboboxInput";
import { ComboboxItem } from "@repo/ui/combobox/comboboxItem";
import { ComboboxSection } from "@repo/ui/combobox/comboboxSection";
import { FieldDescription } from "@repo/ui/field/fieldDescription";
import { Size } from "@repo/ui/helpers/helpers";
import { Input } from "@repo/ui/Inputs/input";
import { Label } from "@repo/ui/Label/label";
import { SimpleMenu } from "@repo/ui/Menu/simpleMenu";
import { SimpleMenuItem } from "@repo/ui/Menu/simpleMenuItem";
import { SimpleMenuPopover } from "@repo/ui/Menu/simpleMenuPopover";
import { AlertDialogVariant } from "@repo/ui/Modal/alertDialog";
import { ConfirmationModal } from "@repo/ui/Modal/confirmationModal";
import { Pill } from "@repo/ui/Pills/pill";
import { selectStyle } from "@repo/ui/Select/style";
import {
  SegmentedControl,
  type SegmentedControlOption,
} from "@repo/ui/toggleButton/segmentedControl";
import {
  IconArrowRight,
  IconPlus,
  IconSelector,
  IconX,
} from "@tabler/icons-react";
import { useEffect, useMemo, useState, type FC, type ReactNode } from "react";
import { Button as AriaButton, MenuTrigger } from "react-aria-components";
import { twMerge } from "tailwind-merge";
import { useAllUserCustomFieldDefinitionsQuery } from "../../api/entities/userCustomField.js";
import { splitTrimmedCsv } from "../../helpers/utilities.js";
import {
  buildDefaultOutcomeForMappingMode,
  buildOutcomeForWorkingModel,
  getDefaultMappingModeForFieldType,
  getDefaultStageName,
  getFieldBackedWorkingModel,
  hasCustomizedRevenueResults,
  getRuleConditionLabel,
  normalizeLegacyCrmOutcomeDefaults,
  shouldConfirmMappingModeChange,
  type FieldBackedOutcomeRule,
  type MappingMode,
  type RuleMappingMode,
} from "./fieldBackedOutcomeMapping.js";

interface FieldBackedOutcomeFieldsProps {
  outcome: OutcomeDefinition | null;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
}

type FunnelStageCategory = Exclude<OutcomeGreenRedYellowResultCategory, "gray">;

interface CategoryOption {
  value: FunnelStageCategory;
  label: string;
  color: typeof Color.Green | typeof Color.Yellow | typeof Color.Red;
}

const categoryOptions: readonly CategoryOption[] = [
  {
    value: greenRedYellowResultTypeCategory.green,
    label: "Green",
    color: Color.Green,
  },
  {
    value: greenRedYellowResultTypeCategory.yellow,
    label: "Yellow",
    color: Color.Yellow,
  },
  {
    value: greenRedYellowResultTypeCategory.red,
    label: "Red",
    color: Color.Red,
  },
];

const ruleMappingModeOptions: readonly SegmentedControlOption<MappingMode>[] = [
  { id: "specific", label: "Exact values" },
  { id: "presence", label: "Field is blank or filled" },
];

const amountMappingModeOption: SegmentedControlOption<MappingMode> = {
  id: "amount",
  label: "Amount",
};

interface MappingModeControlProps {
  value: MappingMode;
  onChange: (mode: MappingMode) => void;
  isReadOnly: boolean;
  supportsAmountMode: boolean;
}

const MappingModeControl: FC<MappingModeControlProps> = (props) => {
  const { value, onChange, isReadOnly, supportsAmountMode } = props;
  const options = supportsAmountMode
    ? [...ruleMappingModeOptions, amountMappingModeOption]
    : ruleMappingModeOptions;

  return (
    <div className="flex items-center gap-3">
      <span className="text-sm font-medium text-foreground">Match using</span>
      <SegmentedControl
        aria-label="How CRM values determine the outcome"
        options={options.map((option) => ({
          ...option,
          isDisabled: isReadOnly,
        }))}
        value={value}
        onChange={onChange}
        size={Size.SMALL}
      />
    </div>
  );
};

const supportedFieldTypes = new Set<CustomFieldDefinition["field_type"]>([
  CustomFieldType.Text,
  CustomFieldType.Number,
  CustomFieldType.Boolean,
  CustomFieldType.Date,
  CustomFieldType.DateAndTime,
]);

const outcomeFieldSections: Partial<Record<CustomFieldResourceType, string>> = {
  [CustomFieldResourceType.CONTACT]: `${customFieldResourceTypeLabels[CustomFieldResourceType.CONTACT]} custom fields`,
  [CustomFieldResourceType.GRADING]: `${customFieldResourceTypeLabels[CustomFieldResourceType.GRADING]} custom fields`,
};

interface OutcomeFieldOption {
  id: string;
  label: string;
}

interface OutcomeFieldOptionSection {
  id: CustomFieldResourceType;
  label: string;
  options: readonly OutcomeFieldOption[];
}

interface OutcomeFieldPickerProps {
  sections: readonly OutcomeFieldOptionSection[];
  value: string | null;
  placeholder: string;
  isDisabled: boolean;
  onChange: (value: string) => void;
}

const OutcomeFieldPicker: FC<OutcomeFieldPickerProps> = (props) => {
  const { sections, value, placeholder, isDisabled, onChange } = props;

  return (
    <Combobox<OutcomeFieldOptionSection>
      defaultItems={sections}
      value={value}
      onChange={(key) => {
        if (key !== null) onChange(String(key));
      }}
      isDisabled={isDisabled}
      aria-label="CRM status field"
    >
      <Label size={Size.SMALL}>CRM status field</Label>
      <ComboboxInput placeholder={placeholder} />
      <ComboboxContent<OutcomeFieldOptionSection>>
        {(section: OutcomeFieldOptionSection) => (
          <ComboboxSection<OutcomeFieldOption>
            id={section.id}
            label={section.label}
            items={section.options}
          >
            {(option: OutcomeFieldOption) => (
              <ComboboxItem id={option.id} textValue={option.label}>
                {option.label}
              </ComboboxItem>
            )}
          </ComboboxSection>
        )}
      </ComboboxContent>
    </Combobox>
  );
};

interface OutcomeResultSelectProps {
  value: FunnelStageCategory;
  accessibleLabel: string;
  isDisabled: boolean;
  onChange: (value: FunnelStageCategory) => void;
}

const OutcomeResultSelect: FC<OutcomeResultSelectProps> = (props) => {
  const { value, accessibleLabel, isDisabled, onChange } = props;
  const selectedOption = categoryOptions.find(
    (option) => option.value === value,
  );

  if (!selectedOption) return null;

  return (
    <MenuTrigger>
      <AriaButton
        aria-label={accessibleLabel}
        isDisabled={isDisabled}
        className={selectStyle().button({
          className: "min-w-0 justify-between gap-1.5 px-1.5 py-0.5",
        })}
      >
        <Pill
          label={selectedOption.label}
          color={selectedOption.color}
          size={Size.SMALL}
          className="shrink-0"
        />
        <IconSelector
          className={selectStyle().chevron({
            className: "mr-0 size-4",
          })}
        />
      </AriaButton>
      <SimpleMenuPopover placement="bottom start">
        <SimpleMenu
          aria-label={accessibleLabel}
          selectionMode="single"
          selectedKeys={[value]}
        >
          {categoryOptions.map((option) => (
            <SimpleMenuItem
              id={option.value}
              key={option.value}
              textValue={option.label}
              onAction={() => onChange(option.value)}
            >
              <Pill
                label={option.label}
                color={option.color}
                size={Size.SMALL}
              />
            </SimpleMenuItem>
          ))}
        </SimpleMenu>
      </SimpleMenuPopover>
    </MenuTrigger>
  );
};

export const FieldBackedOutcomeFields: FC<FieldBackedOutcomeFieldsProps> = ({
  outcome,
  onUpdateOutcome,
  isReadOnly,
}) => {
  const { data: allCustomFieldDefinitions, isLoading } =
    useAllUserCustomFieldDefinitionsQuery();
  // Both CRM mapping outcomes (exact-value funnel and blank/filled presence)
  // are edited by the shared FieldBackedRulesEditor via a single stages + rules working
  // model; revenue (the "Amount" mode) keeps its own fixed rules.
  const rawFieldMappingOutcome = useMemo<
    FunnelOutcomeDefinition | ContactCustomFieldPresenceOutcomeDefinition | null
  >(() => {
    if (!outcome) return null;
    if (
      isFunnelOutcomeDefinition(outcome) ||
      isContactCustomFieldPresenceOutcomeDefinition(outcome)
    ) {
      return outcome;
    }
    return null;
  }, [outcome]);
  const fieldMappingOutcome = useMemo(
    () =>
      rawFieldMappingOutcome
        ? normalizeLegacyCrmOutcomeDefaults(rawFieldMappingOutcome)
        : null,
    [rawFieldMappingOutcome],
  );

  useEffect(() => {
    // Persist the legacy-default upgrade only when editable. In a read-only or
    // suggestion pane onUpdateOutcome still writes through to the draft, so
    // firing here would commit a Won/Lost change the viewer never made.
    // Rendering still uses the normalized fieldMappingOutcome regardless.
    if (
      !isReadOnly &&
      rawFieldMappingOutcome &&
      fieldMappingOutcome &&
      fieldMappingOutcome !== rawFieldMappingOutcome
    ) {
      onUpdateOutcome(fieldMappingOutcome);
    }
  }, [
    fieldMappingOutcome,
    isReadOnly,
    onUpdateOutcome,
    rawFieldMappingOutcome,
  ]);
  const fieldBackedOutcome =
    fieldMappingOutcome ??
    (outcome && isRevenueOutcomeDefinition(outcome) ? outcome : null);
  const selectedFieldId =
    fieldBackedOutcome?.custom_field_definition_id ?? null;

  const eligibleFields = useMemo(
    () =>
      (allCustomFieldDefinitions ?? [])
        .filter(
          (definition) =>
            outcomeFieldSections[definition.resource_type] !== undefined &&
            supportedFieldTypes.has(definition.field_type),
        )
        .sort((left, right) => {
          const resourceOrder =
            left.resource_type === right.resource_type
              ? 0
              : left.resource_type === CustomFieldResourceType.CONTACT
                ? -1
                : 1;
          return (
            resourceOrder ||
            left.display_name.localeCompare(right.display_name, undefined, {
              sensitivity: "base",
            })
          );
        }),
    [allCustomFieldDefinitions],
  );
  const fieldSections = useMemo<readonly OutcomeFieldOptionSection[]>(() => {
    const displayNameCounts = new Map<string, number>();
    for (const definition of eligibleFields) {
      const normalizedName = definition.display_name.trim().toLowerCase();
      displayNameCounts.set(
        normalizedName,
        (displayNameCounts.get(normalizedName) ?? 0) + 1,
      );
    }

    return Object.entries(outcomeFieldSections).flatMap(
      ([resourceType, sectionLabel]) => {
        if (!sectionLabel) return [];

        const options = eligibleFields
          .filter((definition) => definition.resource_type === resourceType)
          .map((definition) => {
            const normalizedName = definition.display_name.trim().toLowerCase();
            const hasDuplicateName =
              (displayNameCounts.get(normalizedName) ?? 0) > 1;
            const sourceLabel =
              customFieldResourceTypeLabels[definition.resource_type];

            return {
              id: definition.id,
              label: hasDuplicateName
                ? `${definition.display_name} · ${sourceLabel}`
                : definition.display_name,
            };
          });

        return options.length > 0
          ? [
              {
                id: resourceType as CustomFieldResourceType,
                label: sectionLabel,
                options,
              },
            ]
          : [];
      },
    );
  }, [eligibleFields]);
  const selectedField = eligibleFields.find(
    (definition) => definition.id === selectedFieldId,
  );

  const handleFieldChange = (fieldId: string): void => {
    const field = eligibleFields.find(
      (definition) => definition.id === fieldId,
    );
    if (!field || field.id === selectedFieldId) return;

    const nextMappingMode = getDefaultMappingModeForFieldType(field.field_type);
    const nextOutcome = buildDefaultOutcomeForMappingMode(
      field.id,
      nextMappingMode,
      field.field_type === CustomFieldType.Boolean,
    );
    onUpdateOutcome(
      fieldMappingOutcome && nextMappingMode !== "amount"
        ? preserveOutcomeTerminology(nextOutcome, fieldMappingOutcome)
        : nextOutcome,
    );
  };

  const hasNoEligibleFields = !isLoading && eligibleFields.length === 0;
  const selectedFieldMissing =
    selectedFieldId !== null && !isLoading && selectedField === undefined;

  return (
    <div className="space-y-6">
      <div className="max-w-md">
        <div className="max-w-sm">
          <OutcomeFieldPicker
            sections={fieldSections}
            value={selectedField?.id ?? null}
            onChange={handleFieldChange}
            placeholder={
              isLoading ? "Loading fields..." : "Select a CRM field..."
            }
            isDisabled={isReadOnly || isLoading || fieldSections.length === 0}
          />
        </div>
        {hasNoEligibleFields ? (
          <p className="mt-2 text-xs text-destructive">
            No supported CRM fields are available.
          </p>
        ) : null}
        {selectedFieldMissing ? (
          <p className="mt-2 text-xs text-destructive">
            The previously selected field is no longer available. Choose a new
            field to continue.
          </p>
        ) : null}
      </div>

      {selectedField && outcome && isRevenueOutcomeDefinition(outcome) ? (
        <RevenueRulesEditor
          outcome={outcome}
          field={selectedField}
          onUpdateOutcome={onUpdateOutcome}
          isReadOnly={isReadOnly}
        />
      ) : null}

      {selectedField && fieldMappingOutcome ? (
        // Key by the backing field so the editor's local input drafts reset when
        // the field changes. Rule ids are deterministic (equals_no, equals_yes,
        // …), so without a fresh mount a reused id would keep the previous
        // field's text and could overwrite the new outcome on the next edit.
        <div className="space-y-6">
          <FieldBackedRulesEditor
            key={fieldMappingOutcome.custom_field_definition_id}
            outcome={fieldMappingOutcome}
            onUpdateOutcome={onUpdateOutcome}
            isReadOnly={isReadOnly}
            usesBooleanDefaults={
              selectedField.field_type === CustomFieldType.Boolean
            }
            supportsAmountMode={
              selectedField.field_type === CustomFieldType.Number
            }
          />
          <PositiveResultTerminology
            outcome={fieldMappingOutcome}
            onUpdateOutcome={onUpdateOutcome}
            isReadOnly={isReadOnly}
          />
        </div>
      ) : null}
    </div>
  );
};

interface PositiveResultTerminologyProps {
  outcome:
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
}

const PositiveResultTerminology: FC<PositiveResultTerminologyProps> = (
  props,
) => {
  const { outcome, onUpdateOutcome, isReadOnly } = props;

  const updateTerminology = (
    field: "singular_noun" | "plural_noun" | "rate_label",
    value: string,
  ): void => {
    onUpdateOutcome({
      ...outcome,
      [field]: value || undefined,
    });
  };

  const normalizeTerminology = (
    field: "singular_noun" | "plural_noun" | "rate_label",
    value: string | undefined,
  ): void => {
    const normalizedValue = value?.trim() || undefined;
    if (normalizedValue === value) return;

    onUpdateOutcome({
      ...outcome,
      [field]: normalizedValue,
    });
  };

  return (
    <section className="@container max-w-5xl border-t border-border-light pt-6">
      <div>
        <p className="text-sm text-muted-foreground">
          Customize how the positive result appears in dashboards and reports.
        </p>
      </div>

      <div className="mt-4 grid max-w-md grid-cols-1 gap-4 [@container(min-width:36rem)]:max-w-none [@container(min-width:36rem)]:grid-cols-3">
        <Input
          label="Singular noun"
          value={outcome.singular_noun ?? ""}
          placeholder="win"
          description='For example, "win".'
          required
          isDisabled={isReadOnly}
          onChange={(event) =>
            updateTerminology("singular_noun", event.target.value)
          }
          onBlur={() =>
            normalizeTerminology("singular_noun", outcome.singular_noun)
          }
          maxLength={100}
        />
        <Input
          label="Plural noun"
          value={outcome.plural_noun ?? ""}
          placeholder="wins"
          description='For example, "wins".'
          required
          isDisabled={isReadOnly}
          onChange={(event) =>
            updateTerminology("plural_noun", event.target.value)
          }
          onBlur={() =>
            normalizeTerminology("plural_noun", outcome.plural_noun)
          }
          maxLength={100}
        />
        <Input
          label="Conversion metric label"
          value={outcome.rate_label ?? ""}
          placeholder="Win rate"
          description='For example, "Win rate".'
          required
          isDisabled={isReadOnly}
          onChange={(event) =>
            updateTerminology("rate_label", event.target.value)
          }
          onBlur={() => normalizeTerminology("rate_label", outcome.rate_label)}
          maxLength={100}
        />
      </div>
    </section>
  );
};

interface RevenueRulesEditorProps {
  outcome: RevenueOutcomeDefinition;
  field: CustomFieldDefinition;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
}

const RevenueRulesEditor: FC<RevenueRulesEditorProps> = (props) => {
  const { outcome, field, onUpdateOutcome, isReadOnly } = props;
  const [pendingMappingMode, setPendingMappingMode] =
    useState<RuleMappingMode | null>(null);
  const fieldName = field.display_name;
  const [wonResult, lostResult, pendingResult] = revenueOutcomeResults;
  const getResultLabel = (defaultResult: {
    value: string;
    label: string;
  }): string =>
    outcome.results.find((result) => result.value === defaultResult.value)
      ?.label ?? defaultResult.label;
  const wonLabel = getResultLabel(wonResult);
  const lostLabel = getResultLabel(lostResult);
  const pendingLabel = getResultLabel(pendingResult);

  const applyMappingMode = (nextMode: RuleMappingMode): void => {
    onUpdateOutcome(
      preserveOutcomeTerminology(
        buildDefaultOutcomeForMappingMode(
          field.id,
          nextMode,
          field.field_type === CustomFieldType.Boolean,
        ),
        outcome,
      ),
    );
  };

  const changeMappingMode = (nextMode: MappingMode): void => {
    if (nextMode === "amount") return;
    if (hasCustomizedRevenueResults(outcome)) {
      setPendingMappingMode(nextMode);
      return;
    }
    applyMappingMode(nextMode);
  };

  return (
    <section className="max-w-5xl space-y-3">
      <MappingModeControl
        value="amount"
        onChange={changeMappingMode}
        isReadOnly={isReadOnly}
        supportsAmountMode
      />
      <RuleSection description="The amount is also retained for revenue reporting.">
        <StaticRule
          condition={`${fieldName} is greater than 0`}
          result={wonLabel}
        />
        <StaticRule
          condition={`${fieldName} is 0 or less`}
          result={lostLabel}
        />
        <StaticRule condition={`${fieldName} is blank`} result={pendingLabel} />
      </RuleSection>
      <ConfirmationModal
        isOpen={pendingMappingMode !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingMappingMode(null);
        }}
        title="Replace this mapping?"
        confirmText="Switch mode"
        variant={AlertDialogVariant.Destructive}
        onConfirm={() => {
          if (pendingMappingMode) applyMappingMode(pendingMappingMode);
          setPendingMappingMode(null);
        }}
      >
        Switching modes will replace the Won, Lost, and Pending labels you
        customized for this outcome.
      </ConfirmationModal>
    </section>
  );
};

const StaticRule: FC<{ condition: string; result: string }> = ({
  condition,
  result,
}) => (
  <div className="flex min-h-10 items-center gap-3 border-b border-border-light py-2 last:border-0">
    <span className="min-w-0 flex-1 text-sm text-foreground">
      When <span className="font-medium">{condition}</span>
    </span>
    <IconArrowRight className="size-4 shrink-0 text-muted-foreground" />
    <span className="w-28 text-sm font-medium text-foreground">{result}</span>
  </div>
);

interface RuleSectionProps {
  description: string;
  children: ReactNode;
}

const RuleSection: FC<RuleSectionProps> = ({ description, children }) => (
  <section className="max-w-2xl space-y-2">
    <FieldDescription>{description}</FieldDescription>
    <div className="border-y border-border-light">{children}</div>
  </section>
);

interface FieldBackedRulesEditorProps {
  outcome:
    | FunnelOutcomeDefinition
    | ContactCustomFieldPresenceOutcomeDefinition;
  onUpdateOutcome: (outcome: OutcomeDefinition) => void;
  isReadOnly: boolean;
  usesBooleanDefaults: boolean;
  supportsAmountMode: boolean;
}

const FieldBackedRulesEditor: FC<FieldBackedRulesEditorProps> = (props) => {
  const {
    outcome,
    onUpdateOutcome,
    isReadOnly,
    usesBooleanDefaults,
    supportsAmountMode,
  } = props;
  // The editor works in a single stages + rules model regardless of which
  // field-backed outcome definition backs it (funnel vs presence).
  const { mappingMode, stages, rules } = useMemo(
    () => getFieldBackedWorkingModel(outcome),
    [outcome],
  );
  const customFieldDefinitionId = outcome.custom_field_definition_id;
  // Exactly one green result is required — it is what green-rate reporting keys
  // on. The result selector can't clear the last green (see the guard in
  // updateRuleResult); deleting the green row promotes the first remaining row
  // instead. Either way the outcome always keeps exactly one green.
  const greenStageCount = stages.filter(
    (stage) => stage.category === greenRedYellowResultTypeCategory.green,
  ).length;
  const [valuesTextByRuleId, setValuesTextByRuleId] = useState<
    Record<string, string>
  >({});
  const [outcomeNameTextByRuleId, setOutcomeNameTextByRuleId] = useState<
    Record<string, string>
  >({});
  const [pendingMappingMode, setPendingMappingMode] =
    useState<MappingMode | null>(null);
  const tableGridColumns =
    mappingMode === "specific"
      ? "grid-cols-[minmax(7rem,0.65fr)_minmax(15rem,3fr)_1rem_minmax(7rem,1.5fr)_6.5rem_2rem]"
      : "grid-cols-[10rem_5rem_minmax(12rem,1fr)_6.5rem]";

  useEffect(() => {
    setValuesTextByRuleId((current) =>
      Object.fromEntries(
        rules.map((rule) => [
          rule.id,
          current[rule.id] ?? rule.values.join(", "),
        ]),
      ),
    );
  }, [rules]);

  const commit = (
    nextStages: readonly FunnelOutcomeStage[],
    nextRules: readonly FieldBackedOutcomeRule[],
  ): void => {
    // A transient working model can be momentarily invalid mid-edit — e.g. the
    // same CRM value typed into two rows fails the funnel schema's uniqueness
    // check, so buildOutcomeForWorkingModel throws. Skip the commit and keep the
    // last valid outcome rather than letting the error escape the input handler
    // (matching the previous funnel editor's guard). The local input text still
    // reflects what was typed, so the next valid edit commits normally.
    let nextOutcome: OutcomeDefinition;
    try {
      nextOutcome = buildOutcomeForWorkingModel(
        customFieldDefinitionId,
        nextStages,
        nextRules,
      );
    } catch {
      return;
    }
    onUpdateOutcome(preserveOutcomeTerminology(nextOutcome, outcome));
  };

  const applyMappingMode = (nextMode: MappingMode): void => {
    if (nextMode === mappingMode) return;

    setValuesTextByRuleId({});
    setOutcomeNameTextByRuleId({});
    const nextOutcome = buildDefaultOutcomeForMappingMode(
      customFieldDefinitionId,
      nextMode,
      usesBooleanDefaults,
    );
    // Revenue carries its own reporting terminology, so the CRM-mapping
    // nouns are not carried over to it.
    onUpdateOutcome(
      nextMode === "amount"
        ? nextOutcome
        : preserveOutcomeTerminology(nextOutcome, outcome),
    );
  };

  const changeMappingMode = (nextMode: MappingMode): void => {
    if (nextMode === mappingMode) return;
    if (shouldConfirmMappingModeChange(stages, rules, mappingMode)) {
      setPendingMappingMode(nextMode);
      return;
    }
    applyMappingMode(nextMode);
  };

  const addRule = (): void => {
    const ruleId = uniqueSlug(
      "rule",
      rules.map((rule) => rule.id),
    );
    const stageId = uniqueSlug(
      `value_${rules.length + 1}`,
      stages.map((stage) => stage.id),
    );
    commit(
      [
        ...stages,
        {
          id: stageId,
          name: `Value ${rules.length + 1}`,
          category: greenRedYellowResultTypeCategory.yellow,
          values: [],
        },
      ],
      [
        ...rules,
        {
          id: ruleId,
          condition: "equals",
          values: [],
          stage_id: stageId,
        },
      ],
    );
  };

  const updateRuleResult = (
    ruleIndex: number,
    result: FunnelStageCategory,
  ): void => {
    const rule = rules[ruleIndex];
    if (!rule) return;
    const currentStage = rule.stage_id
      ? stages.find((stage) => stage.id === rule.stage_id)
      : undefined;

    // Block clearing the last green (see greenStageCount): the positive result
    // is moved by selecting green on another row, which demotes the current
    // green — never cleared, which would leave the outcome with no green.
    const isOnlyGreenStage =
      currentStage?.category === greenRedYellowResultTypeCategory.green &&
      greenStageCount <= 1;
    if (result !== greenRedYellowResultTypeCategory.green && isOnlyGreenStage) {
      return;
    }

    const stageId =
      currentStage?.id ??
      uniqueSlug(
        getDefaultStageName(rule.condition),
        stages.map((stage) => stage.id),
      );
    const stagesWithCurrent = currentStage
      ? stages
      : [
          ...stages,
          {
            id: stageId,
            name: getDefaultStageName(rule.condition),
            category: result,
            values: [],
          },
        ];
    const stageIndex = stagesWithCurrent.findIndex(
      (stage) => stage.id === stageId,
    );
    commit(
      updateStageCategory(stagesWithCurrent, stageIndex, result),
      replaceAt(rules, ruleIndex, { ...rule, stage_id: stageId }),
    );
  };

  return (
    <>
      <section className="max-w-5xl space-y-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <MappingModeControl
            value={mappingMode}
            onChange={changeMappingMode}
            isReadOnly={isReadOnly}
            supportsAmountMode={supportsAmountMode}
          />
          {mappingMode === "specific" ? (
            <Button
              variant={ButtonVariant.Secondary}
              size={Size.SMALL}
              icon={<IconPlus className="size-4" />}
              onClick={addRule}
              isDisabled={isReadOnly}
            >
              Add outcome value
            </Button>
          ) : null}
        </div>

        <div className="overflow-x-auto rounded-lg border border-border-light bg-white shadow-xs">
          <div className="inline-block min-w-full align-top">
            <div
              className={twMerge(
                "grid items-center gap-2 border-b border-border-light px-3 py-2 text-xs font-medium text-muted-foreground",
                tableGridColumns,
              )}
            >
              <span>Condition</span>
              {mappingMode === "specific" ? <span>CRM value</span> : null}
              <span />
              <span>Outcome value</span>
              <span>Result</span>
              {mappingMode === "specific" ? <span /> : null}
            </div>
            <div className="divide-y divide-border-light">
              {rules.map((rule, ruleIndex) => {
                const stage = rule.stage_id
                  ? stages.find((item) => item.id === rule.stage_id)
                  : undefined;
                return (
                  <div
                    key={rule.id}
                    className={twMerge(
                      "grid items-center gap-2 px-3 py-3",
                      tableGridColumns,
                    )}
                  >
                    <span className="text-sm whitespace-nowrap text-foreground">
                      If CRM field {getRuleConditionLabel(rule.condition)}
                    </span>

                    {mappingMode === "specific" &&
                    rule.condition === "equals" ? (
                      <Input
                        id={`field-outcome-values-${rule.id}`}
                        accessibleLabel={`Matching CRM values for rule ${ruleIndex + 1}`}
                        value={
                          valuesTextByRuleId[rule.id] ?? rule.values.join(", ")
                        }
                        placeholder="Enter values"
                        inputClassName="font-mono text-xs"
                        maxLength={1000}
                        isDisabled={isReadOnly}
                        onChange={(event) => {
                          const valuesText = event.target.value;
                          setValuesTextByRuleId((current) => ({
                            ...current,
                            [rule.id]: valuesText,
                          }));
                          commit(
                            stages,
                            replaceAt(rules, ruleIndex, {
                              ...rule,
                              values: splitTrimmedCsv(valuesText),
                            }),
                          );
                        }}
                      />
                    ) : mappingMode === "specific" ? (
                      <span className="text-center text-sm text-muted-foreground">
                        —
                      </span>
                    ) : null}

                    <IconArrowRight
                      className={twMerge(
                        "size-4 justify-self-center text-muted-foreground",
                        mappingMode === "presence" && "-translate-x-2",
                      )}
                    />

                    {stage ? (
                      <Input
                        id={`field-outcome-name-${rule.id}`}
                        accessibleLabel={`Outcome value for rule ${ruleIndex + 1}`}
                        value={outcomeNameTextByRuleId[rule.id] ?? stage.name}
                        placeholder="Outcome value"
                        isDisabled={isReadOnly}
                        maxLength={100}
                        onChange={(event) => {
                          const nextName = event.target.value;
                          setOutcomeNameTextByRuleId((current) => ({
                            ...current,
                            [rule.id]: nextName,
                          }));

                          // A label may be temporarily empty while someone is
                          // replacing it. Keep that transient state local because
                          // an empty saved outcome label is invalid.
                          if (nextName.trim().length === 0) return;

                          commit(
                            stages.map((item) =>
                              item.id === stage.id
                                ? { ...item, name: nextName }
                                : item,
                            ),
                            rules,
                          );
                        }}
                        onBlur={(event) => {
                          const nextName = event.target.value.trim();
                          setOutcomeNameTextByRuleId((current) => {
                            const { [rule.id]: _removed, ...remaining } =
                              current;
                            return remaining;
                          });

                          if (
                            nextName.length === 0 ||
                            nextName === stage.name
                          ) {
                            return;
                          }

                          commit(
                            stages.map((item) =>
                              item.id === stage.id
                                ? { ...item, name: nextName }
                                : item,
                            ),
                            rules,
                          );
                        }}
                      />
                    ) : (
                      <span className="text-sm font-medium text-foreground">
                        N/A
                      </span>
                    )}

                    {stage ? (
                      <OutcomeResultSelect
                        value={stage.category}
                        onChange={(result) =>
                          updateRuleResult(ruleIndex, result)
                        }
                        isDisabled={isReadOnly}
                        accessibleLabel={`Result for rule ${ruleIndex + 1}`}
                      />
                    ) : (
                      <Pill
                        label="N/A"
                        color={Color.Gray}
                        size={Size.SMALL}
                        className="justify-self-start"
                      />
                    )}

                    {mappingMode === "specific" ? (
                      <div className="flex justify-end">
                        <IconButton
                          className="hover:bg-gray-100"
                          variant={ButtonVariant.Tertiary}
                          icon={<IconX className="size-4" />}
                          accessibleLabel={`Remove rule ${ruleIndex + 1}`}
                          isDisabled={isReadOnly || rules.length <= 1}
                          onClick={() => {
                            const nextStages = stage
                              ? stages.filter((item) => item.id !== stage.id)
                              : stages;
                            const nextRules = rules.filter(
                              (_, index) => index !== ruleIndex,
                            );
                            // Deleting the green row promotes the first remaining
                            // row to green so the outcome always keeps exactly one
                            // green result (what green-rate reporting keys on).
                            const hasGreen = nextStages.some(
                              (item) =>
                                item.category ===
                                greenRedYellowResultTypeCategory.green,
                            );
                            commit(
                              hasGreen
                                ? nextStages
                                : nextStages.map((item, index) =>
                                    index === 0
                                      ? {
                                          ...item,
                                          category:
                                            greenRedYellowResultTypeCategory.green,
                                        }
                                      : item,
                                  ),
                              nextRules,
                            );
                          }}
                        />
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
        {mappingMode === "specific" ? (
          <div className="flex items-center gap-2 px-1 text-xs text-muted-foreground">
            <span>All other CRM values are treated as</span>
            <Pill label="N/A" color={Color.Gray} size={Size.SMALL} />
          </div>
        ) : null}
      </section>
      <ConfirmationModal
        isOpen={pendingMappingMode !== null}
        onOpenChange={(isOpen) => {
          if (!isOpen) setPendingMappingMode(null);
        }}
        title="Replace this mapping?"
        confirmText="Switch mode"
        variant={AlertDialogVariant.Destructive}
        onConfirm={() => {
          if (pendingMappingMode) applyMappingMode(pendingMappingMode);
          setPendingMappingMode(null);
        }}
      >
        Switching modes will replace the outcome values and CRM mappings you
        configured in this table.
      </ConfirmationModal>
    </>
  );
};

function preserveOutcomeTerminology<T extends OutcomeDefinition>(
  nextOutcome: T,
  currentOutcome: OutcomeDefinition,
): T {
  return {
    ...nextOutcome,
    singular_noun: currentOutcome.singular_noun,
    plural_noun: currentOutcome.plural_noun,
    rate_label: currentOutcome.rate_label,
  };
}

function replaceAt<T>(values: readonly T[], index: number, value: T): T[] {
  return values.map((current, currentIndex) =>
    currentIndex === index ? value : current,
  );
}

function updateStageCategory(
  stages: readonly FunnelOutcomeStage[],
  stageIndex: number,
  category: FunnelStageCategory,
): readonly FunnelOutcomeStage[] {
  const currentStage = stages[stageIndex];
  if (!currentStage) return stages;
  return category === greenRedYellowResultTypeCategory.green
    ? stages.map((stage, index) => ({
        ...stage,
        category:
          index === stageIndex
            ? greenRedYellowResultTypeCategory.green
            : stage.category === greenRedYellowResultTypeCategory.green
              ? greenRedYellowResultTypeCategory.yellow
              : stage.category,
      }))
    : replaceAt(stages, stageIndex, { ...currentStage, category });
}

function uniqueSlug(base: string, existingIds: readonly string[]): string {
  const normalizedBase = slugify(base);
  let candidate = normalizedBase;
  let suffix = 2;
  while (existingIds.includes(candidate)) {
    candidate = `${normalizedBase}_${suffix}`;
    suffix += 1;
  }
  return candidate;
}

function slugify(value: string): string {
  return (
    value
      .trim()
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "_")
      .replace(/^_+|_+$/g, "") || "value"
  );
}
